// The segment builder's model (UI-4): the rows a marketer edits, and the
// engine definition they stand for. No React here — tests/admin runs this
// file on Node, and the round trip below is what those tests hold.
//
// The shape follows Klaviyo's builder: condition GROUPS joined by ÉS, and
// inside a group, conditions joined by VAGY. That is exactly what the engine
// (emc_db/segments/schema.py) reads as `{"v":1,"all":[group, group, …]}`,
// where a group of one is the condition itself and a group of more is
// `{"any":[…]}`. The engine can hold more than that (a `not`, an `all` inside
// an `any`); a definition written that way elsewhere is kept here as an
// OPAQUE item — shown, removable, never rewritten — so opening and saving a
// segment never changes what it means.
//
// What each field is, and which operators it takes, comes from the engine's
// own whitelist (`GET /segments/fields`); this file only names things the way
// a Klaviyo user expects to read them. The one operator of our own,
// `between_dates`, is two engine conditions (`after` and `before` on the same
// field) and is written as their `all`.

import type { Definition, SegmentField, SegmentFieldKind } from "@/lib/api";

// --- The engine's limits (schema.py), checked here so the marketer reads a
//     sentence before the API has to answer 422 ------------------------------

export const MAX_CONDITIONS = 50;
export const MAX_DAYS = 3650;
export const MAX_IN_VALUES = 500;
export const MAX_TEXT = 320;
export const ATTRIBUTE_KEY_MAX = 120;

/** The engine's rule for an imported attribute's key (fields.py): 1-120
 * characters as the importer wrote them — case, spaces and accents kept —
 * none of them a double quote, a backslash, a control character or half a
 * surrogate pair. Counted by code point, as Python counts. */
export function attributeKeyOk(key: string): boolean {
  const characters = [...key];
  if (characters.length < 1 || characters.length > ATTRIBUTE_KEY_MAX) return false;
  return characters.every((character) => {
    const code = character.codePointAt(0) ?? 0;
    if (character === '"' || code === 0x5c) return false;
    if (code < 0x20 || (code >= 0x7f && code <= 0x9f)) return false;
    return code < 0xd800 || code > 0xdfff;
  });
}
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const MOMENT = /^\d{4}-\d{2}-\d{2}T[0-9:.]+(Z|[+-]\d{2}:\d{2})$/;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;

export const VALUELESS = new Set(["is_empty", "is_not_empty", "is_null", "is_not_null"]);
export const LISTED = new Set(["in", "not_in"]);
export const BETWEEN_DATES = "between_dates";

// --- The catalogue: the engine's whitelist, keyed the way rows name fields --

/** How a row names the attribute template (`attr.<kulcs>` on the wire). */
export const ATTR = "attr";

export interface FieldInfo {
  field: string;
  kind: SegmentFieldKind;
  label: string;
  operators: string[];
  choices: string[];
  /** A bounded whole number's range (the migration wave's 1-7). */
  bounds?: [number, number] | null;
}

export type Catalogue = Map<string, FieldInfo>;

export function catalogueOf(fields: SegmentField[]): Catalogue {
  const map: Catalogue = new Map();
  for (const field of fields) map.set(field.kind === "attribute" ? ATTR : field.field, field);
  return map;
}

/** The row subject for a wire field name: `attr.gender` is the attribute
 * template with the key `gender`. */
export function subjectOf(field: string): { subject: string; attrKey: string } {
  return field.startsWith("attr.")
    ? { subject: ATTR, attrKey: field.slice("attr.".length) }
    : { subject: field, attrKey: "" };
}

// --- What the builder offers, grouped the way Klaviyo groups it -------------

export type Category = "activity" | "letters" | "migration" | "property" | "membership" | "consent" | "place";

export const CATEGORIES: { key: Category; label: string; hint: string }[] = [
  {
    key: "activity",
    label: "Amit csinált (vagy nem csinált)",
    hint: "megnyitás, kattintás, rendelés, kapott levelek",
  },
  {
    key: "letters",
    label: "A leveleink, levelenként",
    hint: "mikor kapott tőlünk, hányat nyitott meg, az utolsó 5 / 10 levélből",
  },
  {
    key: "migration",
    label: "Klaviyo-átvétel",
    hint: "melyik hullámban vettük át a Klaviyótól",
  },
  { key: "property", label: "Tulajdonság", hint: "város, forrás, importált mezők" },
  { key: "membership", label: "Listán / szegmensben van vagy nincs", hint: "lista, másik szegmens" },
  { key: "consent", label: "Hozzájárulás", hint: "feliratkozás állapota, forrása, ideje" },
  { key: "place", label: "Ország / nyelv", hint: "nyelv; az ország a szegmensé" },
];

// Where a number comes from, said where it is chosen: the 90-day counters
// are fed only by our own tracking, never by the Klaviyo import, so on the
// first warmup days they are 0 for nearly everyone.
export const COUNTER_NOTE =
  "A számláló csak a saját küldéseinkből nő — a Klaviyo-import nem tölti, ezért az első hetekben szinte mindenkinél 0. A nyitás és kattintás idejére (utolsó megnyitás / kattintás) a Klaviyo-adat is számít.";
export const RECEIVED_NOTE =
  "Csak a saját küldéseink számítanak, és éjszakánként frissül: a mai küldés holnaptól látszik.";
export const ORDERS_NOTE =
  "A webshop rendeléseiből, amióta a webshop küldi őket (attribúció); a Klaviyo-korszak rendelései nincsenek benne.";
export const SEGMENT_NOTE =
  "Csak ugyanennek az országnak egy kész (felépített) statikus szegmensére lehet hivatkozni — a dinamikusnak nincs tagsága.";
export const LAST_TIME_NOTE =
  "Az utolsó alkalom idejéből számol: „az elmúlt N napban” pontos, de „két dátum között” azt jelenti, hogy utoljára akkor — ha később is volt, nem illik rá.";

// Slice 5.4: the opens without the machines. The plain open fields count every
// open, the way Klaviyo does; these leave out Apple Mail's automatic fetch and
// the mail scanners — and they know nothing of the Klaviyo era, which never
// told the two apart. A Gmail image-proxy fetch counts: Gmail fetches the pixel
// when the person opens the message (`emc_db.ua`).
export const HUMAN_OPEN_NOTE =
  "Csak a saját követőpixelünk nyitásai, gépek nélkül: az Apple Mail automatikus letöltése és a levélszűrők nem számítanak. A Gmail képproxyn át jövő nyitás számít, mert a Gmail akkor tölti le a képet, amikor az ember megnyitja a levelet. A Klaviyóból hozott nyitások ezt nem tudják megkülönböztetni, ezért itt nem szerepelnek — az első hetekben szinte mindenkinél üres.";

// Slice 5.5: the per-message figures (`contact_engagement`). Counted per
// message, not per open; from our own sends only; rebuilt nightly.
export const LETTERS_NOTE =
  "Levelenként számol, csak a saját küldéseinkből, és éjszakánként frissül. Kapottnak az a levél számít, amelynek a kézbesítését a szolgáltató visszaigazolta (vagy még nem jelzett semmit) — a visszapattant, az elutasított és a még késő nem. Megnyitottnak az, amit ember nyitott meg vagy amiben kattintott: az Apple Mail automatikus letöltése és a levélszűrők nem számítanak, a Gmail képproxyja igen. Az időablak a levél küldési ideje. Akinek még nem küldtünk, annál minden szám 0.";

export const NEVER_NOTE = "Akinél ilyen még nem történt (nincs adat), az is ide tartozik.";
export const WAVE_NOTE =
  "A hullám-bélyeg: a waves assign írja, a waves sync ugyanezt a számot adja át a Klaviyónak (emc_wave), és a Klaviyo-kampány az emc_wave ≤ N profilokat zárja ki. A migráció alatt ezért a kampány közönsége „az 1–N. hullámban” — akkor is, ha valaki azóta inaktív lett. Csak az számít átvettnek, akinek a hullámát a waves sync már átadta a Klaviyónak: a még át nem adott bélyeg és a Klaviyo-azonosító nélküli kontakt a Klaviyóé marad.";

/** What to say under a row: the field's own caveat, and the operator's. */
export function notesFor(subject: string, op: string): { text: string; warn: boolean }[] {
  const info = SUBJECT_BY_FIELD.get(subject);
  const notes: { text: string; warn: boolean }[] = [];
  if (info?.note) notes.push({ text: info.note, warn: info.note === COUNTER_NOTE });
  if (info?.activity && ["after", "before", BETWEEN_DATES].includes(op)) {
    notes.push({ text: LAST_TIME_NOTE, warn: false });
  }
  if (op === "not_within_days") notes.push({ text: NEVER_NOTE, warn: false });
  return notes;
}

export interface Subject {
  field: string;
  category: Category;
  label: string;
  note?: string;
  /** `ltv_cents`: typed and shown in EUR, sent in whole cents. */
  money?: boolean;
  /** Activity timestamps read as "did it, when" rather than as a date field. */
  activity?: boolean;
  /** Operator words of its own, when the kind's words would mislead. */
  words?: Record<string, string>;
  /** Where a new row on this subject starts. */
  start?: { op: string; text: string };
}

const WAVE_WORDS: Record<string, string> = {
  lte: "az 1–N. hullámban",
  gte: "az N. vagy későbbi hullámban",
  eq: "pontosan az N. hullámban",
  between: "két hullám között",
  is_not_null: "átvettük (van hulláma)",
  is_null: "még a Klaviyóé (nincs hulláma)",
};

export const SUBJECTS: Subject[] = [
  { field: "last_open_at", category: "activity", label: "Megnyitott levelet", activity: true },
  { field: "last_click_at", category: "activity", label: "Kattintott levélben", activity: true },
  { field: "last_order_at", category: "activity", label: "Rendelt", activity: true, note: ORDERS_NOTE },
  { field: "opens_90d", category: "activity", label: "Megnyitások száma (90 nap)", note: COUNTER_NOTE },
  {
    field: "last_human_open_at",
    category: "activity",
    label: "Valóban megnyitotta (gépek nélkül)",
    activity: true,
    note: HUMAN_OPEN_NOTE,
  },
  {
    field: "human_opens_90d",
    category: "activity",
    label: "Valódi megnyitások száma (90 nap)",
    note: HUMAN_OPEN_NOTE,
  },
  { field: "clicks_90d", category: "activity", label: "Kattintások száma (90 nap)", note: COUNTER_NOTE },
  { field: "emails_received_30d", category: "activity", label: "Kapott levelek (30 nap)", note: RECEIVED_NOTE },
  { field: "orders_count", category: "activity", label: "Rendelések száma", note: ORDERS_NOTE },
  { field: "ltv_cents", category: "activity", label: "Költés összesen (EUR)", money: true, note: ORDERS_NOTE },
  {
    field: "migration_wave",
    category: "migration",
    label: "Átvett hullám",
    note: WAVE_NOTE,
    words: WAVE_WORDS,
    start: { op: "lte", text: "1" },
  },
  { field: "city", category: "property", label: "Város" },
  { field: "source", category: "property", label: "Forrás" },
  { field: "timezone", category: "property", label: "Időzóna" },
  { field: "created_at", category: "property", label: "Profil létrehozva" },
  { field: ATTR, category: "property", label: "Importált tulajdonság" },
  { field: "list", category: "membership", label: "Lista" },
  { field: "segment", category: "membership", label: "Másik szegmens", note: SEGMENT_NOTE },
  { field: "consent_status", category: "consent", label: "Hozzájárulás állapota" },
  { field: "consent_source", category: "consent", label: "Hozzájárulás forrása" },
  { field: "consent_at", category: "consent", label: "Hozzájárulás ideje" },
  { field: "language", category: "place", label: "Nyelv" },
  ...letterSubjects(),
];

// The side table's fields (5.5), in the builder's words. The windows and the
// last-N are generated, so a window the engine adds shows up under its own
// label at the end (`offeredSubjects`) rather than missing.
function letterSubjects(): Subject[] {
  const windows: [number, string][] = [
    [30, "30 nap"],
    [60, "60 nap"],
    [90, "90 nap"],
    [180, "180 nap"],
    [365, "1 év"],
  ];
  const figures: [string, string][] = [
    ["received", "Kapott levelek száma"],
    ["opened", "Megnyitott levelek száma"],
    ["clicked", "Levelek, amelyekben kattintott"],
  ];
  const letter = (field: string, label: string, activity = false): Subject => ({
    field,
    category: "letters",
    label,
    note: LETTERS_NOTE,
    ...(activity ? { activity: true } : {}),
  });
  return [
    letter("last_sent_at", "Kapott tőlünk levelet", true),
    letter("first_sent_at", "Először kapott tőlünk levelet", true),
    letter("last_delivered_at", "Kézbesített levelet kapott", true),
    letter("received_total", "Kapott levelek száma (összesen)"),
    ...figures.flatMap(([figure, words]) =>
      windows.map(([days, span]) => letter(`${figure}_${days}d`, `${words} (${span})`)),
    ),
    letter("last5_opened", "Az utolsó 5 levélből megnyitott"),
    letter("last5_clicked", "Az utolsó 5 levélből kattintott"),
    letter("last10_opened", "Az utolsó 10 levélből megnyitott"),
    letter("last10_clicked", "Az utolsó 10 levélből kattintott"),
    letter("last_human_click_at", "Valóban kattintott (gépek nélkül)", true),
  ];
}

const SUBJECT_BY_FIELD = new Map(SUBJECTS.map((subject) => [subject.field, subject]));

export function subjectInfo(subject: string): Subject | undefined {
  return SUBJECT_BY_FIELD.get(subject);
}

/** The builder's name for a field — ours when we have one, the engine's when not. */
export function subjectLabel(subject: string, catalogue?: Catalogue): string {
  return SUBJECT_BY_FIELD.get(subject)?.label ?? catalogue?.get(subject)?.label ?? subject;
}

/** Every subject the engine offers right now, in the builder's order, with
 * any whitelist field this file does not know yet at the end. */
export function offeredSubjects(catalogue: Catalogue): Subject[] {
  const known = SUBJECTS.filter((subject) => catalogue.has(subject.field));
  const extra = [...catalogue.values()]
    .filter((info) => info.kind !== "attribute" && !SUBJECT_BY_FIELD.has(info.field))
    .map((info): Subject => ({ field: info.field, category: "property", label: info.label || info.field }));
  return [...known, ...extra];
}

// --- Operators, in words ----------------------------------------------------

const TIME_ACTIVITY: Record<string, string> = {
  within_days: "az elmúlt N napban",
  not_within_days: "nem az elmúlt N napban",
  is_not_null: "valaha",
  is_null: "soha",
  after: "egy nap után",
  before: "utoljára egy nap előtt",
  [BETWEEN_DATES]: "utoljára két nap között",
};
const TIME_PLAIN: Record<string, string> = {
  within_days: "az elmúlt N napban",
  not_within_days: "nem az elmúlt N napban",
  after: "egy nap után",
  before: "egy nap előtt",
  [BETWEEN_DATES]: "két nap között",
  is_not_null: "meg van adva",
  is_null: "nincs megadva",
};
const INT: Record<string, string> = {
  gte: "legalább",
  lte: "legfeljebb",
  eq: "pontosan",
  between: "között",
};
const TEXT: Record<string, string> = {
  eq: "pontosan",
  ne: "nem",
  in: "egyike ezeknek",
  not_in: "egyik sem ezek közül",
  starts_with: "ezzel kezdődik",
  is_empty: "üres",
  is_not_empty: "ki van töltve",
};
const ENUM: Record<string, string> = {
  eq: "pontosan",
  ne: "nem",
  in: "egyike ezeknek",
  not_in: "egyik sem ezek közül",
};
const ATTRIBUTE: Record<string, string> = {
  eq: "pontosan",
  ne: "nem (vagy nincs megadva)",
  in: "egyike ezeknek",
  is_empty: "nincs megadva",
  is_not_empty: "meg van adva",
};
const LIST: Record<string, string> = { in: "rajta van", not_in: "nincs rajta" };
const SEGMENT: Record<string, string> = { in: "benne van", not_in: "nincs benne" };

function wordsFor(subject: string, kind: SegmentFieldKind): Record<string, string> {
  const own = SUBJECT_BY_FIELD.get(subject)?.words;
  if (own) return own;
  if (kind === "time") return SUBJECT_BY_FIELD.get(subject)?.activity ? TIME_ACTIVITY : TIME_PLAIN;
  if (kind === "int") return INT;
  if (kind === "enum") return ENUM;
  if (kind === "attribute") return ATTRIBUTE;
  if (kind === "membership") return subject === "segment" ? SEGMENT : LIST;
  return TEXT;
}

export interface OperatorChoice {
  op: string;
  label: string;
}

/** The operators a subject takes — the engine's list, in the order the words
 * table gives them, plus `between_dates` wherever both `after` and `before`
 * are allowed. An operator the engine added and this file has no word for is
 * still offered, under its own name. */
export function operatorsFor(subject: string, catalogue: Catalogue): OperatorChoice[] {
  const info = catalogue.get(subject);
  if (!info) return [];
  const words = wordsFor(subject, info.kind);
  const allowed = new Set(info.operators);
  if (allowed.has("after") && allowed.has("before")) allowed.add(BETWEEN_DATES);
  const ordered = Object.keys(words).filter((op) => allowed.has(op));
  const rest = [...allowed].filter((op) => !(op in words)).sort();
  return [...ordered, ...rest].map((op) => ({ op, label: words[op] ?? op }));
}

const TIME_OPS = new Set(["within_days", "not_within_days", "before", "after", "is_null", "is_not_null", BETWEEN_DATES]);
const NUMBER_OPS = new Set(["gte", "lte", "between"]);

/** The words for an operator. Without the catalogue the operator itself says
 * which kind of field it is on — the time and number operators belong to one
 * kind each, and `eq`/`in` read the same on the rest. */
export function operatorLabel(subject: string, op: string, catalogue?: Catalogue): string {
  const kind = catalogue?.get(subject)?.kind ?? kindOf(subject, op);
  return wordsFor(subject, kind)[op] ?? op;
}

function kindOf(subject: string, op: string): SegmentFieldKind {
  if (subject === ATTR) return "attribute";
  if (subject === "list" || subject === "segment") return "membership";
  if (TIME_OPS.has(op)) return "time";
  if (NUMBER_OPS.has(op)) return "int";
  return "text";
}

/** The words for a consent state, the way the contact page says them. */
export const CONSENT_WORDS: Record<string, string> = {
  subscribed: "feliratkozott",
  unsubscribed: "leiratkozott",
  never_subscribed: "soha nem iratkozott fel",
  pending: "megerősítésre vár",
};

// --- A row, and the builder -------------------------------------------------

export interface ConditionDraft {
  id: string;
  /** The field name, or ATTR for an imported attribute (the key is `attrKey`). */
  subject: string;
  attrKey: string;
  /** An engine operator, or BETWEEN_DATES. */
  op: string;
  /** One value as typed: text, a number, a day count, a date, euros. */
  text: string;
  /** Several values: `in` / `not_in` (text, enum, attribute), or list and segment ids. */
  list: string[];
  /** `between` of numbers, or the two days of BETWEEN_DATES. */
  low: string;
  high: string;
}

export interface GroupDraft {
  id: string;
  kind: "group";
  /** Joined by VAGY. */
  conditions: ConditionDraft[];
  /** The definition held this single condition inside an `any` of its own;
   * kept so that opening and saving changes nothing. */
  wrapped?: boolean;
}

export interface OpaqueDraft {
  id: string;
  kind: "opaque";
  node: unknown;
}

export type ItemDraft = GroupDraft | OpaqueDraft;

/** Items joined by ÉS. */
export interface BuilderDraft {
  items: ItemDraft[];
}

let sequence = 0;
function nextId(prefix: string): string {
  sequence += 1;
  return `${prefix}-${sequence}`;
}

export function emptyCondition(subject = "", op = ""): ConditionDraft {
  return { id: nextId("c"), subject, attrKey: "", op, text: "", list: [], low: "", high: "" };
}

export function newGroup(conditions: ConditionDraft[] = [emptyCondition()]): GroupDraft {
  return { id: nextId("g"), kind: "group", conditions };
}

/** A condition that starts on a sensible operator for its subject. */
export function conditionFor(subject: string, catalogue: Catalogue): ConditionDraft {
  const ops = operatorsFor(subject, catalogue).map((choice) => choice.op);
  const start = subjectInfo(subject)?.start;
  if (start && ops.includes(start.op)) return { ...emptyCondition(subject, start.op), text: start.text };
  const preferred = ["within_days", "gte", "eq", "in"].find((op) => ops.includes(op));
  const condition = emptyCondition(subject, preferred ?? ops[0] ?? "");
  if (condition.op === "within_days") condition.text = "30";
  if (condition.op === "gte") condition.text = subjectInfo(subject)?.money ? "" : "1";
  return condition;
}

// --- Values: typed text to the engine's values, and back --------------------

export function eurosToCents(text: string): number | null {
  const compact = text.trim().replace(/\s/g, "").replace(",", ".");
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(compact);
  if (!match) return null;
  const whole = Number(match[2]);
  const fraction = Number((match[3] ?? "").padEnd(2, "0"));
  const cents = whole * 100 + fraction;
  if (!Number.isSafeInteger(cents)) return null;
  return match[1] === "-" ? -cents : cents;
}

export function centsToEuros(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const size = Math.abs(cents);
  const whole = Math.floor(size / 100);
  const fraction = size % 100;
  return fraction === 0 ? `${sign}${whole}` : `${sign}${whole},${String(fraction).padStart(2, "0")}`;
}

function wholeNumber(text: string): number | null {
  const compact = text.trim().replace(/\s/g, "");
  if (!/^-?\d+$/.test(compact)) return null;
  const value = Number(compact);
  return Number.isSafeInteger(value) ? value : null;
}

function isDay(text: string): boolean {
  if (!DATE.test(text)) return false;
  const [year, month, day] = text.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

function shiftDay(text: string, days: number): string {
  const [year, month, day] = text.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return date.toISOString().slice(0, 10);
}

/** The engine's `schema._text`: counted by code point, as Python counts (an
 * emoji is one character, not two), and no half of a surrogate pair. */
function checkText(value: string): string | null {
  const characters = [...value];
  if (characters.length > MAX_TEXT) return `Legfeljebb ${MAX_TEXT} karakter lehet.`;
  if (CONTROL.test(value)) return "Az érték vezérlőkaraktert tartalmaz; írd be újra.";
  const halfPair = characters.some((character) => {
    const code = character.codePointAt(0) ?? 0;
    return code >= 0xd800 && code <= 0xdfff;
  });
  if (halfPair) return "Az érték egy sérült (fél) karaktert tartalmaz; írd be újra.";
  return null;
}

export type Built = { ok: true; node: Definition } | { ok: false; error: string };

function fail(error: string): Built {
  return { ok: false, error };
}

function leaf(field: string, op: string, value: unknown): Built {
  return { ok: true, node: { field, op, value } as Definition };
}

/** One row as the engine's node, or the sentence that says what to fix. */
export function buildCondition(condition: ConditionDraft, catalogue: Catalogue): Built {
  const info = catalogue.get(condition.subject);
  if (!condition.subject || !info) return fail("Válaszd ki, mire szűrjön a feltétel.");
  const op = condition.op;
  const allowed = operatorsFor(condition.subject, catalogue).some((choice) => choice.op === op);
  if (!op || !allowed) return fail("Válassz műveletet.");

  let field = condition.subject;
  if (info.kind === "attribute") {
    // As it is: the importer kept Klaviyo's header exactly, spaces included.
    const key = condition.attrKey;
    if (!attributeKeyOk(key)) {
      return fail(
        `A tulajdonság neve 1–${ATTRIBUTE_KEY_MAX} karakter lehet, idézőjel, visszaper és vezérlőkarakter nélkül — pontosan úgy, ahogy az importban szerepel.`,
      );
    }
    field = `attr.${key}`;
  }

  if (VALUELESS.has(op)) return leaf(field, op, null);

  if (info.kind === "time") {
    if (op === "within_days" || op === "not_within_days") {
      const days = wholeNumber(condition.text);
      if (days === null || days < 1 || days > MAX_DAYS) {
        return fail(`Adj meg egy napszámot 1 és ${MAX_DAYS} között.`);
      }
      return leaf(field, op, days);
    }
    if (op === BETWEEN_DATES) {
      const from = condition.low.trim();
      const to = condition.high.trim();
      if (!isDay(from) || !isDay(to)) return fail("Adj meg két dátumot (év-hó-nap).");
      if (from > to) return fail("A kezdő nap nem lehet a záró nap után.");
      // Both days count: after the first day's midnight, before the midnight
      // that ends the last one.
      return {
        ok: true,
        node: {
          all: [
            { field, op: "after", value: from },
            { field, op: "before", value: shiftDay(to, 1) },
          ],
        } as Definition,
      };
    }
    const moment = condition.text.trim();
    if (!isDay(moment) && !MOMENT.test(moment)) return fail("Adj meg egy dátumot (év-hó-nap).");
    return leaf(field, op, moment);
  }

  if (info.kind === "int") {
    const money = subjectInfo(condition.subject)?.money ?? false;
    const read = (text: string) => (money ? eurosToCents(text) : wholeNumber(text));
    const message = money
      ? "Adj meg egy összeget euróban, legfeljebb két tizedessel."
      : "Adj meg egy egész számot.";
    const outside = (value: number) =>
      info.bounds ? value < info.bounds[0] || value > info.bounds[1] : false;
    const range = info.bounds ? `Válassz ${info.bounds[0]} és ${info.bounds[1]} között.` : "";
    if (op === "between") {
      const low = read(condition.low);
      const high = read(condition.high);
      if (low === null || high === null) return fail(info.bounds ? range : message);
      if (outside(low) || outside(high)) return fail(range);
      if (low > high) return fail("Az alsó határ nem lehet nagyobb a felsőnél.");
      return leaf(field, op, [low, high]);
    }
    const value = read(condition.text);
    if (value === null) return fail(info.bounds ? range : message);
    if (outside(value)) return fail(range);
    return leaf(field, op, value);
  }

  if (info.kind === "membership") {
    const ids = condition.list.map((item) => wholeNumber(item));
    if (ids.length === 0) {
      return fail(condition.subject === "segment" ? "Válassz legalább egy szegmenst." : "Válassz legalább egy listát.");
    }
    if (ids.some((id) => id === null || id < 1)) return fail("Érvénytelen azonosító a választásban.");
    if (ids.length > MAX_IN_VALUES) return fail(`Legfeljebb ${MAX_IN_VALUES} elem választható.`);
    return leaf(field, op, ids);
  }

  // Text, enum and attribute values.
  if (LISTED.has(op)) {
    const values = condition.list;
    if (values.length === 0) return fail("Adj meg legalább egy értéket.");
    if (values.length > MAX_IN_VALUES) return fail(`Legfeljebb ${MAX_IN_VALUES} érték adható meg.`);
    for (const value of values) {
      const problem = checkText(value);
      if (problem) return fail(problem);
      if (info.kind === "enum" && !info.choices.includes(value)) return fail("Válassz a felsoroltak közül.");
    }
    return leaf(field, op, [...values]);
  }
  const value = condition.text;
  if (value.trim() === "") {
    return fail(
      op === "starts_with"
        ? "Adj meg egy kezdőszöveget."
        : "Adj meg egy értéket — üres mezőre az „üres” művelet szűr.",
    );
  }
  const problem = checkText(value);
  if (problem) return fail(problem);
  if (info.kind === "enum" && !info.choices.includes(value)) return fail("Válassz a felsoroltak közül.");
  return leaf(field, op, value);
}

// --- The whole builder ------------------------------------------------------

export type Assembled =
  | { ok: true; definition: Definition; conditions: number }
  | { ok: false; errors: Record<string, string>; general: string | null };

/** Every leaf in a stored node, depth first — how the engine counts and
 * orders them (`compile_leaves`). */
export function leavesIn(node: unknown): number {
  if (node === null || typeof node !== "object") return 0;
  const record = node as Record<string, unknown>;
  if (Array.isArray(record.all)) return record.all.reduce((sum: number, child) => sum + leavesIn(child), 0);
  if (Array.isArray(record.any)) return record.any.reduce((sum: number, child) => sum + leavesIn(child), 0);
  if ("not" in record) return leavesIn(record.not);
  return typeof record.field === "string" ? 1 : 0;
}

export function toDefinition(draft: BuilderDraft, catalogue: Catalogue): Assembled {
  const errors: Record<string, string> = {};
  const all: unknown[] = [];
  let conditions = 0;
  for (const item of draft.items) {
    if (item.kind === "opaque") {
      all.push(item.node);
      conditions += leavesIn(item.node);
      continue;
    }
    const built: Definition[] = [];
    for (const condition of item.conditions) {
      const result = buildCondition(condition, catalogue);
      if (result.ok) {
        built.push(result.node);
        conditions += leavesIn(result.node);
      } else {
        errors[condition.id] = result.error;
      }
    }
    if (item.conditions.length === 0) continue;
    if (built.length !== item.conditions.length) continue;
    all.push(built.length === 1 && !item.wrapped ? built[0] : { any: built });
  }
  if (Object.keys(errors).length > 0) return { ok: false, errors, general: null };
  if (all.length === 0) {
    return { ok: false, errors, general: "Adj hozzá legalább egy feltételt — feltétel nélkül a szegmens az egész ország lenne." };
  }
  if (conditions > MAX_CONDITIONS) {
    return { ok: false, errors, general: `Legfeljebb ${MAX_CONDITIONS} feltétel lehet egy szegmensben (most ${conditions}).` };
  }
  return { ok: true, definition: { v: 1, all } as Definition, conditions };
}

// --- …and back: a stored definition as rows --------------------------------

type Leaf = { field: string; op: string; value?: unknown };

function isLeaf(node: unknown): node is Leaf {
  if (node === null || typeof node !== "object" || Array.isArray(node)) return false;
  const record = node as Record<string, unknown>;
  const keys = Object.keys(record);
  return (
    typeof record.field === "string" &&
    typeof record.op === "string" &&
    keys.every((key) => key === "field" || key === "op" || key === "value")
  );
}

function onlyKey(node: unknown, key: string): unknown[] | null {
  if (node === null || typeof node !== "object" || Array.isArray(node)) return null;
  const record = node as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length !== 1 || keys[0] !== key || !Array.isArray(record[key])) return null;
  return record[key] as unknown[];
}

function asText(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

/** A stored leaf as a row, or null when the row could not say it exactly. */
function conditionFromLeaf(node: Leaf): ConditionDraft | null {
  const { subject, attrKey } = subjectOf(node.field);
  const condition = emptyCondition(subject, node.op);
  condition.attrKey = attrKey;
  const value = node.value;
  if (VALUELESS.has(node.op)) return value === null || value === undefined ? condition : null;
  if (Array.isArray(value)) {
    if (node.op === "between") {
      if (value.length !== 2 || !value.every((item) => typeof item === "number")) return null;
      const money = subjectInfo(subject)?.money ?? false;
      condition.low = money ? centsToEuros(value[0] as number) : String(value[0]);
      condition.high = money ? centsToEuros(value[1] as number) : String(value[1]);
      return condition;
    }
    const items = value.map(asText);
    if (items.some((item) => item === null)) return null;
    // A list id and a list of text look alike here; the kind decides on the
    // way back, and a number that was text would not survive — refuse it.
    const numeric = subject === "list" || subject === "segment";
    if (numeric !== value.every((item) => typeof item === "number")) return null;
    condition.list = items as string[];
    return condition;
  }
  if (typeof value === "number") {
    if (subjectInfo(subject)?.money) condition.text = centsToEuros(value);
    else condition.text = String(value);
    return condition;
  }
  if (typeof value === "string") {
    condition.text = value;
    return condition;
  }
  return null;
}

/** `{"all":[after A, before B]}` on one field, both days: the row BETWEEN_DATES. */
function betweenFrom(node: unknown): ConditionDraft | null {
  const children = onlyKey(node, "all");
  if (!children || children.length !== 2) return null;
  const [first, second] = children;
  if (!isLeaf(first) || !isLeaf(second)) return null;
  if (first.field !== second.field || first.op !== "after" || second.op !== "before") return null;
  if (typeof first.value !== "string" || typeof second.value !== "string") return null;
  if (!isDay(first.value) || !isDay(second.value)) return null;
  const { subject, attrKey } = subjectOf(first.field);
  const condition = emptyCondition(subject, BETWEEN_DATES);
  condition.attrKey = attrKey;
  condition.low = first.value;
  condition.high = shiftDay(second.value, -1);
  // The first day must not come after the last, or the row could not be
  // saved back the way it was read.
  return condition.low <= condition.high ? condition : null;
}

function rowFrom(node: unknown): ConditionDraft | null {
  if (isLeaf(node)) return conditionFromLeaf(node);
  return betweenFrom(node);
}

export function fromDefinition(definition: unknown): BuilderDraft {
  const all = onlyKeyOrV(definition);
  const items: ItemDraft[] = [];
  for (const node of all) {
    const row = rowFrom(node);
    if (row) {
      items.push({ id: nextId("g"), kind: "group", conditions: [row] });
      continue;
    }
    const alternatives = onlyKey(node, "any");
    if (alternatives && alternatives.length > 0) {
      const rows = alternatives.map(rowFrom);
      if (rows.every((candidate): candidate is ConditionDraft => candidate !== null)) {
        items.push({ id: nextId("g"), kind: "group", conditions: rows, wrapped: rows.length === 1 });
        continue;
      }
    }
    items.push({ id: nextId("o"), kind: "opaque", node });
  }
  return { items };
}

function onlyKeyOrV(definition: unknown): unknown[] {
  if (definition === null || typeof definition !== "object") return [];
  const all = (definition as { all?: unknown }).all;
  return Array.isArray(all) ? all : [];
}

/** How many of the builder's rows came through as themselves. */
export function opaqueCount(draft: BuilderDraft): number {
  return draft.items.filter((item) => item.kind === "opaque").length;
}

// --- Explaining a sample: which rows a contact met -------------------------

export interface Slot {
  itemId: string;
  conditionId: string | null;
  /** The positions of this row's leaves in the preview's `matched` flags. */
  leaves: number[];
}

/** Where each row's flags sit in `matched` — the same depth-first order the
 * engine's `compile_leaves` uses over the definition `toDefinition` built. */
export function slotsOf(draft: BuilderDraft): Slot[] {
  const slots: Slot[] = [];
  let position = 0;
  for (const item of draft.items) {
    if (item.kind === "opaque") {
      const size = leavesIn(item.node);
      slots.push({ itemId: item.id, conditionId: null, leaves: range(position, size) });
      position += size;
      continue;
    }
    for (const condition of item.conditions) {
      const size = condition.op === BETWEEN_DATES ? 2 : 1;
      slots.push({ itemId: item.id, conditionId: condition.id, leaves: range(position, size) });
      position += size;
    }
  }
  return slots;
}

function range(start: number, size: number): number[] {
  return Array.from({ length: size }, (_, index) => start + index);
}

/** Names for the ids a row holds: lists and segments are numbered apart,
 * so list 1 and segment 1 are two things. */
export interface Names {
  lists?: Map<string, string>;
  segments?: Map<string, string>;
}

/** The rows of the VAGY groups a sampled contact met: the reason it is in.
 * A group of one is met by everybody in the sample and says nothing. */
export function reasonsFor(draft: BuilderDraft, matched: boolean[], names?: Names): string[] {
  const met = new Set<string>();
  for (const slot of slotsOf(draft)) {
    if (slot.conditionId !== null && slot.leaves.every((index) => matched[index] === true)) {
      met.add(slot.conditionId);
    }
  }
  const reasons: string[] = [];
  for (const item of draft.items) {
    if (item.kind !== "group" || item.conditions.length < 2) continue;
    for (const condition of item.conditions) {
      if (met.has(condition.id)) reasons.push(describeCondition(condition, names));
    }
  }
  return reasons;
}

// --- Words for a whole condition --------------------------------------------

function listWords(values: string[], names?: Map<string, string>): string {
  const shown = values.slice(0, 3).map((value) => names?.get(value) ?? value);
  return values.length > 3 ? `${shown.join(", ")} és még ${values.length - 3}` : shown.join(", ");
}

/** "Kattintott levélben az elmúlt 30 napban", for a sample's reason and a list's summary. */
export function describeCondition(condition: ConditionDraft, names?: Names): string {
  const subject =
    condition.subject === ATTR ? `${condition.attrKey || "tulajdonság"}` : subjectLabel(condition.subject);
  const op = condition.op;
  const words = operatorLabel(condition.subject, op);
  if (VALUELESS.has(op)) return `${subject}: ${words}`;
  if (op === BETWEEN_DATES) return `${subject}: ${words} (${condition.low} – ${condition.high})`;
  if (op === "between") return `${subject}: ${condition.low} és ${condition.high} között`;
  // "az elmúlt N napban", "az 1–N. hullámban": the value goes where the N is.
  if (!LISTED.has(op) && words.includes("N")) return `${subject}: ${words.replace("N", condition.text)}`;
  if (LISTED.has(op)) {
    const consent = condition.subject === "consent_status";
    const values = consent ? condition.list.map((value) => CONSENT_WORDS[value] ?? value) : condition.list;
    const lookup =
      condition.subject === "list" ? names?.lists : condition.subject === "segment" ? names?.segments : undefined;
    return `${subject}: ${words} — ${listWords(values, lookup)}`;
  }
  const value = condition.subject === "consent_status" ? (CONSENT_WORDS[condition.text] ?? condition.text) : condition.text;
  const unit = subjectInfo(condition.subject)?.money ? " €" : "";
  return `${subject}: ${words} ${value}${unit}`;
}

/** A definition in one line: groups joined by ÉS, alternatives by VAGY. */
export function summarize(definition: unknown, names?: Names): string {
  const draft = fromDefinition(definition);
  const parts = draft.items.map((item) => {
    if (item.kind === "opaque") return "(összetett feltétel)";
    const alternatives = item.conditions.map((condition) => describeCondition(condition, names));
    return alternatives.length > 1 ? `(${alternatives.join(" VAGY ")})` : (alternatives[0] ?? "");
  });
  return parts.join(" ÉS ");
}

// --- One-click starting points ---------------------------------------------

export interface PresetParameter {
  label: string;
  min: number;
  max: number;
  initial: number;
  definitionFor: (value: number) => Definition;
  nameFor: (value: number) => string;
}

export interface Preset {
  key: string;
  /** "migration": a campaign's audience during the handover. "warmup": the
   * rules `waves assign` stamps by — who the next wave takes over. "letters":
   * the per-message figures of our own sends (5.5). */
  group: "migration" | "warmup" | "klaviyo" | "letters";
  name: string;
  description: string;
  /** With a parameter, the definition for its initial value. */
  definition: Definition;
  parameter?: PresetParameter;
}

const within = (field: string, days: number) => ({ field, op: "within_days", value: days });
const active = (days: number) => ({ any: [within("last_open_at", days), within("last_click_at", days)] });
const takenOver = (last: number): Definition => ({
  v: 1,
  all: [{ field: "migration_wave", op: "lte", value: last }],
});
// Klaviyo's "Engaged (N days)": can receive email marketing, and opened or
// clicked an email in the last N days — every open, Apple Mail's automatic
// ones included (slice 5.4). `last_human_open_at` gives the same window with
// the machine opens left out.
const ENGAGED_WINDOWS = [30, 60, 90, 180] as const;
const engaged = (days: number, opens: "last_open_at" | "last_human_open_at"): Definition => ({
  v: 1,
  all: [
    { field: "consent_status", op: "eq", value: "subscribed" },
    { any: [within(opens, days), within("last_click_at", days)] },
  ],
});

export const PRESETS: Preset[] = [
  // The audience of a regular campaign during the handover: whoever the
  // waves 1..N have stamped. `waves sync` gives Klaviyo the same number and
  // Klaviyo excludes `emc_wave <= N`, so the two sides split the list exactly;
  // a stamped contact who has gone quiet stays ours, an unstamped one who
  // has just become active stays Klaviyo's.
  {
    key: "taken-over",
    group: "migration",
    name: "Átvett hullámok (1–1)",
    description:
      "Akit az 1–N. hullámban már átvettünk a Klaviyótól. Pontosan őket zárja ki a Klaviyo (emc_wave ≤ N): nálunk kapnak levelet, ott nem — akkor is, ha azóta inaktívak.",
    definition: takenOver(1),
    parameter: {
      label: "Az utolsó átvett hullám",
      min: 1,
      max: 7,
      initial: 1,
      definitionFor: takenOver,
      nameFor: (last) => `Átvett hullámok (1–${last})`,
    },
  },
  // The rules of `emc_worker/warmup/waves.py`, as ordinary conditions: who
  // the next `waves assign` stamps, and how many. They nest (a 30-day clicker
  // is also active in 90 days). Not a campaign's audience during the handover:
  // engagement moves daily, the stamp does not.
  {
    key: "wave-1",
    group: "warmup",
    name: "1. hullám — 30 napon belül kattintott",
    description: "Kit vesz át az 1. hullám: aki az elmúlt 30 napban kattintott egy levelünkben.",
    definition: { v: 1, all: [within("last_click_at", 30)] },
  },
  {
    key: "wave-2",
    group: "warmup",
    name: "2. hullám — 30 napon belül nyitott vagy kattintott",
    description: "Kit vesz át a 2. hullám: aki az elmúlt 30 napban nyitott vagy kattintott.",
    definition: { v: 1, all: [active(30)] },
  },
  {
    key: "wave-3-4",
    group: "warmup",
    name: "3–4. hullám — 90 napon belül nyitott vagy kattintott",
    description: "Kit vesz át a 3–4. hullám: aki az elmúlt 90 napban nyitott vagy kattintott.",
    definition: { v: 1, all: [active(90)] },
  },
  {
    key: "wave-5-6",
    group: "warmup",
    name: "5–6. hullám — 180 napon belül aktív",
    description: "Kit vesz át az 5–6. hullám: aki az elmúlt 180 napban nyitott vagy kattintott.",
    definition: { v: 1, all: [active(180)] },
  },
  {
    key: "wave-7",
    group: "warmup",
    name: "7. hullám — 12 hónapon belül látott",
    description:
      "Kit vesz át a 7. hullám: aki egy éven belül nyitott, kattintott vagy feliratkozott — a 12+ hónapja passzívak nélkül.",
    definition: {
      v: 1,
      all: [
        {
          any: [within("last_open_at", 365), within("last_click_at", 365), within("consent_at", 365)],
        },
      ],
    },
  },
  ...ENGAGED_WINDOWS.map(
    (days): Preset => ({
      key: `engaged-${days}`,
      group: "klaviyo",
      name: `Engaged ${days} nap`,
      description: `A Klaviyo „Engaged” szegmense: feliratkozott, és az elmúlt ${days} napban megnyitott vagy kattintott egy levelet. Minden nyitás számít, az Apple Mail automatikus nyitása is — ahogy a Klaviyóban.`,
      definition: engaged(days, "last_open_at"),
    }),
  ),
  ...ENGAGED_WINDOWS.map(
    (days): Preset => ({
      key: `engaged-${days}-human`,
      group: "klaviyo",
      name: `Engaged ${days} nap — csak valódi nyitás`,
      description: `Feliratkozott, és az elmúlt ${days} napban ember nyitotta meg a levelünket vagy kattintott benne: az Apple Mail és a levélszűrők gépi nyitása nem számít, a Gmail képproxyn át jövő nyitás igen. Csak a saját küldéseinkből — a Klaviyo-korszak nyitásait nem tudja szétválasztani.`,
      definition: engaged(days, "last_human_open_at"),
    }),
  ),
  {
    key: "unengaged-180",
    group: "klaviyo",
    name: "180 napja nem aktív",
    description: "Sem nem nyitott, sem nem kattintott az elmúlt 180 napban (vagy soha) — újraaktiváláshoz vagy kizáráshoz.",
    definition: {
      v: 1,
      all: [
        { field: "last_open_at", op: "not_within_days", value: 180 },
        { field: "last_click_at", op: "not_within_days", value: 180 },
      ],
    },
  },
  {
    key: "never-engaged",
    group: "klaviyo",
    name: "Soha nem nyitott és nem kattintott",
    description: "Nincs róla megnyitás vagy kattintás — sem a Klaviyóból, sem tőlünk.",
    definition: {
      v: 1,
      all: [
        { field: "last_open_at", op: "is_null", value: null },
        { field: "last_click_at", op: "is_null", value: null },
      ],
    },
  },
  {
    key: "new-subscribers",
    group: "klaviyo",
    name: "Új feliratkozók (30 nap)",
    description: "Aki az elmúlt 30 napban adott hozzájárulást.",
    definition: { v: 1, all: [within("consent_at", 30)] },
  },
  {
    key: "buyers",
    group: "klaviyo",
    name: "Vásárlók",
    description: "Legalább egy rendelés a webshopból.",
    definition: { v: 1, all: [{ field: "orders_count", op: "gte", value: 1 }] },
  },
  {
    key: "repeat-buyers",
    group: "klaviyo",
    name: "Visszatérő vásárlók",
    description: "Legalább két rendelés.",
    definition: { v: 1, all: [{ field: "orders_count", op: "gte", value: 2 }] },
  },
  {
    key: "vip",
    group: "klaviyo",
    name: "VIP — legalább 500 € költés",
    description: "Az összes rendelése együtt legalább 500 euró (az összeg átírható).",
    definition: { v: 1, all: [{ field: "ltv_cents", op: "gte", value: 50_000 }] },
  },
  {
    key: "win-back",
    group: "klaviyo",
    name: "Visszacsábítás — vásárolt, de 180 napja nem",
    description: "Van rendelése, de az utolsó 180 napnál régebbi.",
    definition: {
      v: 1,
      all: [
        { field: "orders_count", op: "gte", value: 1 },
        { field: "last_order_at", op: "not_within_days", value: 180 },
      ],
    },
  },
  // Slice 5.5: from the per-message figures of our own sends. A few obvious
  // ones; the fields are all in the builder for the rest.
  ...([30, 90] as const).map(
    (days): Preset => ({
      key: `letters-opened-${days}`,
      group: "letters",
      name: `Aktív (valódi nyitás) — ${days} nap`,
      description: `Az elmúlt ${days} napban küldött leveleink közül legalább egyet ember nyitott meg vagy kattintott benne. Az Apple Mail és a levélszűrők gépi nyitása nem számít, a Gmail képproxyja igen.`,
      definition: { v: 1, all: [{ field: `opened_${days}d`, op: "gte", value: 1 }] },
    }),
  ),
  {
    key: "letters-quiet-5",
    group: "letters",
    name: "Kapott levelet, de az utolsó 5-ből egyet sem nyitott meg",
    description:
      "Az elmúlt évben legalább 5 levelünket megkapta, és a legutóbbi 5-öt ember nem nyitotta meg (kattintás sem volt bennük) — újraaktiváláshoz vagy pihentetéshez.",
    definition: {
      v: 1,
      all: [
        { field: "received_365d", op: "gte", value: 5 },
        { field: "last5_opened", op: "eq", value: 0 },
      ],
    },
  },
  {
    key: "letters-never-sent",
    group: "letters",
    name: "Még nem kapott tőlünk levelet",
    description:
      "Egyetlen levelünk sem ment ki neki ebből az országból. A Klaviyo-korszak levelei itt nem számítanak.",
    definition: { v: 1, all: [{ field: "last_sent_at", op: "is_null", value: null }] },
  },
];

// --- The engine's reasons, in Hungarian --------------------------------------
//
// The engine (emc_db/segments) explains itself in English, and the page shows
// that sentence as written — it is what the log and the API say. Above it, for
// the reasons a marketer actually meets, the same thing in Hungarian. An
// unknown message gets no gloss rather than a wrong one.

const STATE_WORDS: Record<string, string> = {
  defined: "definiálva",
  requested: "kérve",
  materialising: "épül",
  ready: "kész",
  stale: "elavult",
  failed: "hiba",
  archived: "archiválva",
};

function segmentName(id: string, names?: Map<string, string>): string {
  const name = names?.get(id);
  return name ? `„${name}”` : `#${id}`;
}

export function engineSays(message: string, names?: Map<string, string>): string | null {
  const text = message.trim();
  let match: RegExpExecArray | null;
  if ((match = /^no such list in ([A-Z]{2}): \[(.*)\]$/.exec(text))) {
    return `A feltételben olyan lista szerepel, amely ebben az országban (${match[1]}) nem létezik. Válaszd ki újra a listát.`;
  }
  if ((match = /^no such segment: \[(.*)\]$/.exec(text))) {
    return "A feltételben olyan szegmens szerepel, amely már nem létezik. Válaszd ki újra.";
  }
  if (/^segment \d+ refers to itself$/.test(text)) {
    return "A szegmens nem hivatkozhat saját magára.";
  }
  if ((match = /^segment (\d+) refers back to \d+: a reference cycle$/.exec(text))) {
    return `Körkörös hivatkozás: a(z) ${segmentName(match[1]!, names)} szegmens visszahivatkozik erre. Az egyikből vedd ki a másikat.`;
  }
  if ((match = /^segment (\d+) is ([A-Z]{2}), this one is ([A-Z]{2})$/.exec(text))) {
    return `A(z) ${segmentName(match[1]!, names)} szegmens másik ország (${match[2]}) szegmense; csak azonos országúra lehet hivatkozni.`;
  }
  if ((match = /^segment (\d+) is dynamic and has no set to be a member of$/.exec(text))) {
    return `A(z) ${segmentName(match[1]!, names)} szegmens dinamikus — csak statikus, felépített szegmensre lehet hivatkozni.`;
  }
  if ((match = /^segment (\d+) is (\w+), not ready; its members are the previous definition's$/.exec(text))) {
    const state = STATE_WORDS[match[2]!] ?? match[2];
    return `A hivatkozott ${segmentName(match[1]!, names)} szegmens nincs kész (${state}): a tagsága még a korábbi feltételeié. Előbb azt építsd újra, aztán ezt.`;
  }
  if (/^stuck materialising past \d+s$/.test(text)) {
    return "Az építés elakadt — a folyamat, amelyik végezte, leállt. Indítsd újra az építést.";
  }
  if (/is materialising; a save now would leave its members and its definition apart/.test(text)) {
    return "A tagság épp épül a mentett feltételekből. Várd meg, amíg elkészül, aztán ments.";
  }
  if (/was saved by someone else in the meantime/.test(text)) {
    return "Közben valaki más is elmentette ezt a szegmenst. Töltsd újra az oldalt, és a változtatásodat azon ismételd meg.";
  }
  if (/is archived and cannot be edited$|^segment \d+ is archived$/.test(text)) {
    return "Archivált szegmens: nem módosítható és nem számolható újra.";
  }
  if ((match = /is named by segment\(s\) \[(.*)\]; archive or edit those first$/.exec(text))) {
    const named = match[1]!.split(",").map((id) => segmentName(id.trim(), names));
    return `Másik szegmens hivatkozik rá (${named.join(", ")}). Előbb azt módosítsd vagy archiváld.`;
  }
  if (/: (requested|materialising) -> archived is not allowed$/.test(text)) {
    return "Építés közben nem archiválható — várd meg, amíg elkészül vagy elbukik.";
  }
  if (/is static; its size is its build/.test(text)) {
    return "Statikus szegmens: a darabszáma az építésből jön — kérj újraépítést.";
  }
  return null;
}

/** After the country changes, the chosen lists and segments are another
 * country's: the rows stay, their choices go, and the row asks again. */
export function forgetMemberships(draft: BuilderDraft): BuilderDraft {
  return {
    items: draft.items.map((item) =>
      item.kind === "group"
        ? {
            ...item,
            conditions: item.conditions.map((condition) =>
              condition.subject === "list" || condition.subject === "segment"
                ? { ...condition, list: [] }
                : condition,
            ),
          }
        : item,
    ),
  };
}
